/**
 * AccountSidebar Component
 *
 * Left pane showing mailbox tree navigation with accounts and folders.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { EmailAccount, EmailFolder } from '@/services/emailApi';

interface AccountSidebarProps {
  accounts: EmailAccount[];
  accountFolders: Record<string, EmailFolder[]>;
  expandedAccounts: Set<string>;
  selectedAccountId: string | null;
  selectedFolderId: string | null;
  showSidebar: boolean;
  onToggleSidebar: () => void;
  onToggleAccountExpand: (accountId: string) => void;
  onSelectFolder: (accountId: string, folderId: string) => void;
  onAddAccount: () => void;
}

// Folder icons and priority
const FOLDER_ICON: Record<string, string> = {
  inbox: '📥', sent: '📤', drafts: '📝', trash: '🗑️', spam: '⚠️',
  archive: '📦', starred: '⭐', important: '🔴', all: '📬', custom: '🏷️',
};

const FOLDER_ORDER: Record<string, number> = {
  inbox: 0, sent: 1, drafts: 2, starred: 3, important: 4,
  spam: 5, trash: 6, archive: 7, all: 8, custom: 9,
};

// Clean display name
const getDisplayName = (folder: EmailFolder): string => {
  const name = folder.path || folder.name;
  return name.replace(/^\[Gmail\]\/?/, '').replace(/^INBOX$/i, 'Inbox');
};

const AccountSidebar: React.FC<AccountSidebarProps> = ({
  accounts,
  accountFolders,
  expandedAccounts,
  selectedAccountId,
  selectedFolderId,
  showSidebar,
  onToggleSidebar,
  onToggleAccountExpand,
  onSelectFolder,
  onAddAccount,
}) => {
  const { t } = useTranslation('email');

  return (
    <>
      {/* LEFT PANE: Mailbox Tree Navigation */}
      <div className={`
        ${showSidebar ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static absolute inset-y-0 left-0 z-30
        w-64 bg-white border-r border-gray-200 flex flex-col
        transition-transform duration-300 ease-in-out
        shadow-lg lg:shadow-none flex-shrink-0
      `}>
        <div className="flex-1 overflow-y-auto py-2">
          {/* Mailbox Tree - Each account is a root node */}
          {accounts.map(account => {
            const isExpanded = expandedAccounts.has(account.id);
            const accFolders = accountFolders[account.id] || [];
            const totalUnread = accFolders.reduce((sum, f) => sum + (f.unread_count || 0), 0);

            // Sort folders
            const sortedFolders = [...accFolders].sort(
              (a, b) => (FOLDER_ORDER[a.folder_type] ?? 9) - (FOLDER_ORDER[b.folder_type] ?? 9)
            );

            return (
              <div key={account.id} className="mb-2">
                {/* Account Header (Root Node) */}
                <button
                  onClick={() => onToggleAccountExpand(account.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition ${
                    selectedAccountId === account.id ? 'bg-gray-50' : ''
                  }`}
                >
                  {/* Expand/Collapse Arrow */}
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>

                  {/* Mailbox Icon */}
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>

                  {/* Email Address */}
                  <span className="flex-1 text-sm font-medium text-gray-900 truncate text-left">
                    {account.email}
                  </span>

                  {/* Unread Badge */}
                  {totalUnread > 0 && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-600 text-white flex-shrink-0">
                      {totalUnread}
                    </span>
                  )}
                </button>

                {/* Folder List (Children) - Indented */}
                {isExpanded && (
                  <div className="ml-5 pl-2 border-l border-gray-200">
                    {sortedFolders.map(folder => {
                      const isActive = selectedFolderId === folder.id && selectedAccountId === account.id;
                      const icon = FOLDER_ICON[folder.folder_type] || '📁';
                      const displayName = getDisplayName(folder);

                      return (
                        <button
                          key={folder.id}
                          onClick={() => onSelectFolder(account.id, folder.id)}
                          className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-left transition ${
                            isActive
                              ? 'bg-blue-100 text-blue-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm flex-shrink-0">{icon}</span>
                            <span className="text-sm truncate">{displayName}</span>
                          </div>
                          {folder.unread_count > 0 && (
                            <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-blue-600 text-white flex-shrink-0 ml-1">
                              {folder.unread_count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add Account Button */}
        <div className="border-t border-gray-200 p-3">
          <button
            onClick={onAddAccount}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {t('account_setup.add_account')}
          </button>
        </div>
      </div>

      {/* Sidebar backdrop */}
      {showSidebar && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-20"
          onClick={onToggleSidebar}
        />
      )}
    </>
  );
};

export default AccountSidebar;
